import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, Observable, take } from 'rxjs';
import {
    clearPostOfferCandidatesInformation,
    exportPermanentCandidates,
    initializePermanentCandidatesConfirmedPage,
    initializePermanentCandidatesPreStartPage,
    loadPermanentCandidatesConfirmedPage,
    loadPermanentCandidatesPreStartPage,
    openPermOfferBillingUpdateModalFromGrid,
    openPermPostOfferConfirmNotStartingModalFromGrid,
    setJobOfferStatusToConfirmed
} from 'src/app/root-store/perm-post-offer-workflow-store/actions';
import { defaultPermCandidatesFetchArgs } from 'src/app/root-store/perm-post-offer-workflow-store/perm-post-offer-workflow.reducer';
import { PagingTokenFixed } from 'src/app/shared/models/core';
import { PermanentCandidateItemModel } from 'src/app/shared/models/permanent-candidate-item.model';
import { showToasterMessage } from '../../../shared/actions/toaster.actions';
import { PermCandidatesGridFetchArgs } from '../../../root-store/perm-post-offer-workflow-store/models/perm-post-offer-candidates-grid-filter.model';
import { OptionModel } from '../../../shared/grid/filters/option.model';
import { PermanentCandidatesAlertsFilterEnum } from '../../perm-post-offer/enums/permanent-candidates-alerts.enum';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import * as permPostOfferCandidateWorkflowSelectors from 'src/app/root-store/perm-post-offer-workflow-store/perm-post-offer-candidate-workflow.selectors';
import { FirstNameLastNamePipe } from './pipes/first-name-last-name.pipe';
import { GridNumberRangeFilterPopupComponent } from '../../../shared/grid/filters/grid-number-range-filter-popup.component';
import { GridNumberFilterComponent } from '../../../shared/grid/filters/grid-number-filter.component';
import { GridTextFilterComponent } from '../../../shared/grid/filters/grid-text-filter.component';
import { GridClearFilterPopupComponent } from '../../../shared/grid/filters/grid-clear-filter-popup.component';
import { GridDateRangeFilterPopupComponent } from '../../../shared/grid/filters/grid-date-range-filter-popup.component';
import { GridOptionListFilterPopupComponent } from '../../../shared/grid/filters/grid-option-list-filter-popup.component';
import { GridFilterLayoutComponent } from '../../../shared/grid/filters/base-filter-cell-layout.component';
import { GridColumnDirective } from '../../../shared/grid/grid-column.directive';
import { NgIf, NgClass, CurrencyPipe, AsyncPipe, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { GridAssignedToIdFilterComponent } from '../../../shared/grid/filters/grid-assigned-to-id-filter.component';
import { GridComponent } from '../../../shared/grid/grid.component';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

export enum PermCandidatesGridMode {
    PreStart,
    Confirmed
}

export const PermanentCandidatesGridComponentErrors = {
    NoDiscountBillRate: {
        message: 'Please ensure Final Billing Amount is set before confirming the start date.',
        duration: 4000
    }
};

@UntilDestroy()
@Component({
    selector: 'nova-permanent-candidates-grid',
    templateUrl: './permanent-candidates-grid.component.html',
    styleUrls: ['./permanent-candidates-grid.component.scss'],
    standalone: true,
    imports: [
        GridComponent,
        GridAssignedToIdFilterComponent,
        MatButtonModule,
        NgIf,
        GridColumnDirective,
        GridFilterLayoutComponent,
        GridOptionListFilterPopupComponent,
        GridDateRangeFilterPopupComponent,
        NgClass,
        GridClearFilterPopupComponent,
        GridTextFilterComponent,
        GridNumberFilterComponent,
        RouterLink,
        GridNumberRangeFilterPopupComponent,
        CurrencyPipe,
        AsyncPipe,
        DatePipe,
        FirstNameLastNamePipe
    ]
})
export class PermanentCandidatesGridComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input({ required: true }) gridMode: PermCandidatesGridMode;
    PermCandidatesGridMode = PermCandidatesGridMode;

    isLoading$: Observable<boolean>;
    data$: Observable<PagingTokenFixed<PermanentCandidateItemModel[]>>;
    fetchArgs$: Observable<PermCandidatesGridFetchArgs>;
    defaultFetchArgs: PermCandidatesGridFetchArgs = structuredClone(defaultPermCandidatesFetchArgs);
    selectedRowIds: number[] = [];

    alertsFilterOptions: OptionModel[] = [
        {
            id: PermanentCandidatesAlertsFilterEnum.None,
            name: 'No Alerts'
        },
        {
            id: PermanentCandidatesAlertsFilterEnum.IsStartDateTodayOrPast,
            name: 'Candidates Past Start Date'
        }
    ];

    showAMFacilitiesOnly = true;
    changeFacilityColumnName = this._featureManager.isEnabledSignal(
        FeatureFlag.DisplayPristineFacilityColumnInFacilityGrid
    );

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly store: Store,
        private readonly _router: Router,
        private readonly _featureManager: LDFeatureManager
    ) {}

    ngOnInit(): void {
        this.initialize();
        this.isLoading$ = this.store.select(
            permPostOfferCandidateWorkflowSelectors.selectCandidatesInformationIsLoading
        );
    }

    ngOnDestroy(): void {
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => {
                this.store.dispatch(clearPostOfferCandidatesInformation());
            });
    }

    ngAfterViewInit(): void {
        this._changeDetector.detectChanges();
    }

    load(args: PermCandidatesGridFetchArgs): void {
        if (this.gridMode === PermCandidatesGridMode.PreStart) {
            this.store.dispatch(loadPermanentCandidatesPreStartPage({ args }));
        } else if (this.gridMode === PermCandidatesGridMode.Confirmed) {
            this.store.dispatch(loadPermanentCandidatesConfirmedPage({ args }));
        }
    }

    confirmStartForRow(candidateModel: PermanentCandidateItemModel): void {
        if (!candidateModel.discountedBillingAmount) {
            this.store.dispatch(showToasterMessage(PermanentCandidatesGridComponentErrors.NoDiscountBillRate));
            return;
        }

        this.store.dispatch(setJobOfferStatusToConfirmed({ jobOfferId: candidateModel.permanentJobOfferId }));
    }

    export(): void {
        this.store.dispatch(
            exportPermanentCandidates({
                permanentJobOfferIds: this.selectedRowIds,
                gridMode: this.gridMode
            })
        );
    }

    rowSelected(rows: PermanentCandidateItemModel[]) {
        const ids: number[] = [];
        rows.forEach((r) => {
            ids.push(r.permanentJobOfferId);
        });
        this.selectedRowIds = ids;
    }

    handleEdit(permanentCandidateItemModel: PermanentCandidateItemModel) {
        this.store.dispatch(openPermOfferBillingUpdateModalFromGrid({ model: permanentCandidateItemModel }));
    }

    handleRemove(permanentCandidateItemModel: PermanentCandidateItemModel) {
        this.store.dispatch(openPermPostOfferConfirmNotStartingModalFromGrid({ model: permanentCandidateItemModel }));
    }

    private initialize() {
        if (this.gridMode === PermCandidatesGridMode.PreStart) {
            this.store.dispatch(initializePermanentCandidatesPreStartPage());
            this.data$ = this.store.select(permPostOfferCandidateWorkflowSelectors.selectPreStartCandidatesInformation);
            this.fetchArgs$ = this.store.select(
                permPostOfferCandidateWorkflowSelectors.selectPreStartCandidatesInformationArgs
            );
        } else if (this.gridMode === PermCandidatesGridMode.Confirmed) {
            this.store.dispatch(initializePermanentCandidatesConfirmedPage());
            this.data$ = this.store.select(
                permPostOfferCandidateWorkflowSelectors.selectConfirmedCandidatesInformation
            );
            this.fetchArgs$ = this.store.select(
                permPostOfferCandidateWorkflowSelectors.selectConfirmedCandidatesInformationArgs
            );
        }
    }
}
